import { AwardingMethodCode } from '@/queries/program_purses/schemas'
import { z } from 'zod'

export const SubTypeSchema = z.object({
  id: z.number(),
  source_type: z.number(),
  name: z.string(),
})

export const FundingSourceSchema = z.object({
  id: z.number(),
  name: z.string(),
  sub_types: z.array(SubTypeSchema),
})

// Schema for the global_grade_level nested object
const GlobalGradeLevelSchema = z.object({
  id: z.number(),
  status: z.number(),
  name: z.string(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
})

// Schema for individual program purse grade level items
const ProgramPurseGradeLevelSchema = z.object({
  id: z.number(),
  program_purse_id: z.number(),
  grade_level_id: z.number(),
  amount: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  grade_level_name: z.string(),
  global_grade_level: GlobalGradeLevelSchema,
})

const GlobalEducationTypeSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  created_at: z.string(),
})

const EducationDistributionSchema = z.object({
  id: z.number(),
  program_purse_id: z.number(),
  education_type_id: z.number(),
  education_awarding_type_code: z.string(),
  education_awarding_type_name: z.string(),
  base_amount: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  education_awarding_type: GlobalEducationTypeSchema,
})

const FixedDistributionSchema = z.object({
  id: z.number(),
  program_purse_id: z.number(),
  base_amount: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
})

export const PurseSchema = z.object({
  id: z.number(),
  status: z.number(),
  program_id: z.number(),
  source_type: z.number(),
  sub_source_type: z.number(),
  external_provider_id: z.string().nullable(),
  name: z.string(),
  total: z.number(),
  balance_available: z.number(),
  balance_distribuited: z.number(),
  created_at: z.string(), // ISO 8601 datetime string
  updated_at: z.string(), // ISO 8601 datetime string
  can_distribute_funds_automatically: z.boolean(),
  grade_distributions: z.array(ProgramPurseGradeLevelSchema).optional(),
  education_distributions: z.array(EducationDistributionSchema).optional(),
  fixed_distribution: FixedDistributionSchema.nullable().optional(),
  awarding_method_code: AwardingMethodCode.nullable(),
  awarding_method_name: z.string().nullable(),
})
export type Purse = z.infer<typeof PurseSchema>

const PurseLogSchema = z.object({
  id: z.number(),
  program_purse_id: z.number(),
  purse_id: z.number().nullable(),
  user_id: z.number(),
  type: z
    .number()
    .transform<'Initial' | 'Deposit' | 'Distribution' | 'Unknown'>((val) => {
      if (val === 1) return 'Initial'
      if (val === 2) return 'Deposit'
      if (val === 3) return 'Distribution'
      return 'Unknown'
    }),
  sender: z.string().nullable(),
  amount: z.number(),
  created_at: z.string().datetime(),
})

export const PurseDetailSchema = PurseSchema.extend({
  logs: z.array(PurseLogSchema).or(z.undefined()),
})

export type PurseDetail = z.infer<typeof PurseDetailSchema>

// Schema for the entire array
const ProgramPurseGradeLevelsSchema = z.array(ProgramPurseGradeLevelSchema)

// Export schemas
export {
  GlobalGradeLevelSchema,
  ProgramPurseGradeLevelSchema,
  ProgramPurseGradeLevelsSchema,
}

// Type inference
export type GlobalGradeLevel = z.infer<typeof GlobalGradeLevelSchema>
export type ProgramPurseGradeLevel = z.infer<
  typeof ProgramPurseGradeLevelSchema
>
export type ProgramPurseGradeLevels = z.infer<
  typeof ProgramPurseGradeLevelsSchema
>

// Example usage:
// const result = ProgramPurseGradeLevelsSchema.parse(yourJsonData);
// const safeResult = ProgramPurseGradeLevelsSchema.safeParse(yourJsonData);

export type FundingSource = z.infer<typeof FundingSourceSchema>
